import React, { useEffect, useMemo, useState } from 'react';
import { Warning } from '@phosphor-icons/react';
import { api } from '../api';
import {
  BRANDS, SERVICE_OPTIONS, LOCATION_LABELS, LOCATION_IDS, LOCATION_SUBLABELS,
  DEAL_STATUS_OPTIONS, COLOUR_MAP, STAGE_COLS, STAGE_LABELS, statusLabel,
  formatDate, formatTime, titleCase, stageProgress,
} from '../helpers';
import { showToast } from '../components/Toast';
import { useCarModels } from '../useCarModels';
import { SalespersonSelect } from '../components/EditableCell';

const OTHER_VALUE = '__other__';
const TIME_SLOTS = ['8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM'];

const initialForm = {
  brand: 'KIA',
  model: '',          // model = picked from list (or empty if free-text brand)
  customModel: '',    // free-text model (USED CARS / SERVICE / Other)
  modelIsOther: false,  // true when user picked "+ Other" in the dropdown
  stockDigits: '',
  stockFree: '',
  ro_number: '',
  colour: '',
  delivery_date: '',
  delivery_time: '',
  location: 'south',
  deal_status: 'SOLD',
  services: ['Detail'],
  is_ev: false,
  salesperson: '',
  notes: '',
};

export default function AddVehicle({ onCreated, onUpdate }) {
  const [form, setForm]     = useState(initialForm);
  const [saving, setSaving] = useState(false);
  // The same car entered twice: the one already in the workflow, if any
  const [dupe, setDupe] = useState(null);
  const [dupeOverride, setDupeOverride] = useState(false);
  const { grouped: modelsByBrand } = useCarModels();

  const brandCfg = useMemo(
    () => BRANDS.find((b) => b.id === form.brand) || BRANDS[0],
    [form.brand]
  );
  const modelList = modelsByBrand[form.brand] || null;
  const isFreeModel = brandCfg.freeModel;
  const isService   = form.brand === 'SERVICE';

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  function changeBrand(newBrand) {
    setForm((f) => ({
      ...f,
      brand: newBrand,
      // Reset model when brand switches
      model: '',
      customModel: '',
      modelIsOther: false,
      // Reset stock — different formats per brand
      stockDigits: '',
      stockFree: '',
    }));
  }

  function changeModel(val) {
    if (val === OTHER_VALUE) {
      setForm((f) => ({ ...f, model: '', modelIsOther: true, customModel: '' }));
    } else {
      setForm((f) => ({ ...f, model: val, modelIsOther: false, customModel: '' }));
    }
  }

  function toggleService(s) {
    setForm((f) => ({
      ...f,
      services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s],
    }));
  }

  // Stock-number value displayed in the input (digits only) and the final
  // composed value sent to the API (prefix + digits, or free text for SERVICE)
  function stockOnChange(e) {
    if (isService) {
      set('stockFree', e.target.value.toUpperCase().slice(0, 12));
    } else if (brandCfg.alphanumeric) {
      // GAC etc: keep letters + digits (VIN end), uppercase
      const cleaned = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, brandCfg.digits);
      set('stockDigits', cleaned);
    } else {
      // strip non-digits
      const onlyDigits = e.target.value.replace(/\D/g, '').slice(0, brandCfg.digits);
      set('stockDigits', onlyDigits);
    }
  }

  function composedStock() {
    if (isService) return form.stockFree.trim();
    if (!form.stockDigits) return '';
    return brandCfg.prefix + form.stockDigits;
  }

  function stockValid() {
    if (isService) return form.stockFree.trim().length >= 3;
    return form.stockDigits.length === brandCfg.digits;
  }

  // Once the stock number is complete, ask whether that car is already in the
  // workflow. Server-side because a salesperson only sees their own deals and
  // would never spot a colleague's entry. Debounced; late replies are dropped.
  const stock = composedStock();
  const checkable = stockValid();
  useEffect(() => {
    if (!checkable) { setDupe(null); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      api.lookupStock(stock)
        .then((r) => { if (!cancelled) { setDupe(r.existing); setDupeOverride(false); } })
        .catch(() => { if (!cancelled) setDupe(null); });
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [stock, checkable]);

  // The warning sits by the Stock Number field, near the top of a long form —
  // on submit the toast fires at the bottom of the screen and the panel saying
  // *which* car is off-screen, so bring it into view.
  function revealDupe() {
    requestAnimationFrame(() => {
      document.querySelector('.dupe-warn')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  async function submit(e) {
    e.preventDefault();
    if (saving) return;

    const finalModel = (isFreeModel || form.modelIsOther
      ? form.customModel
      : form.model).trim();
    const finalStock = composedStock();

    if (!finalModel)         return showToast('Pick or type a model');
    if (!stockValid())       return showToast(`Stock #: ${brandCfg.stockHint}`);
    // Without a date the car lands outside every week view — easy to lose
    if (!form.delivery_date) return showToast('Pick a delivery date');
    if (dupe && !dupeOverride) {
      showToast(`${dupe.stock_number} is already in the workflow — open it to make changes`);
      return revealDupe();
    }

    setSaving(true);
    try {
      await api.createVehicle({
        allow_duplicate: dupeOverride,
        brand:        form.brand,
        model:        finalModel,
        stock_number: finalStock,
        ro_number:    form.ro_number || null,
        colour:       form.colour || null,
        location:     form.location,
        deal_status:  form.deal_status,
        delivery_date: form.delivery_date || null,
        delivery_time: form.delivery_time || null,
        services:     form.services,
        is_ev:        form.is_ev,
        salesperson:  form.salesperson || null,
        notes:        form.notes || null,
      });
      showToast('Vehicle added to workflow!');
      setForm(initialForm);
      setDupe(null);
      setDupeOverride(false);
      onCreated?.();
    } catch (err) {
      // Someone else entered the same car while this form was open
      if (err.status === 409 && err.body?.existing) {
        const ex = err.body.existing;
        setDupe(ex);
        setDupeOverride(false);
        showToast(`${ex.stock_number} — ${ex.brand} ${ex.model} is already in the workflow`);
        revealDupe();
      } else {
        showToast('Failed: ' + err.message);
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="page-bg">
      <div className="ph">
        <div>
          <div className="pt">Add Vehicle</div>
          <div className="ps">Register a new vehicle into the workflow pipeline</div>
        </div>
      </div>

      <form className="af" onSubmit={submit}>
        {/* Status (deal type) + Salesperson */}
        <div className="fg2">
          <div>
            <label className="afl">Status</label>
            <select
              className="afi"
              style={{ cursor: 'pointer' }}
              value={form.deal_status}
              onChange={(e) => set('deal_status', e.target.value)}
            >
              {DEAL_STATUS_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="afl">Salesperson</label>
            <SalespersonSelect
              className="afi"
              value={form.salesperson}
              onChange={(val) => set('salesperson', val || '')}
            />
          </div>
        </div>

        {/* Brand (drives model + stock format) */}
        <div className="fg2">
          <div>
            <label className="afl">Brand</label>
            <select
              className="afi"
              style={{ cursor: 'pointer' }}
              value={form.brand}
              onChange={(e) => changeBrand(e.target.value)}
            >
              {BRANDS.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
            </select>
          </div>
          <div>
            <label className="afl">Model</label>
            {isFreeModel ? (
              <input
                className="afi"
                placeholder={isService ? 'e.g. Hilux / Ranger' : 'Type model'}
                value={form.customModel}
                onChange={(e) => set('customModel', e.target.value)}
              />
            ) : (
              <>
                <select
                  className="afi"
                  style={{ cursor: 'pointer' }}
                  value={form.modelIsOther ? OTHER_VALUE : form.model}
                  onChange={(e) => changeModel(e.target.value)}
                >
                  <option value="">— Select model —</option>
                  {(modelList || []).map((m) => <option key={m}>{m}</option>)}
                  <option value={OTHER_VALUE}>+ Other (type custom)…</option>
                </select>
                {form.modelIsOther && (
                  <input
                    className="afi"
                    style={{ marginTop: 8 }}
                    placeholder="Type the model name"
                    value={form.customModel}
                    onChange={(e) => set('customModel', e.target.value)}
                    autoFocus
                  />
                )}
              </>
            )}
          </div>
        </div>

        {/* Stock + RO */}
        <div className="fg2">
          <div>
            <label className="afl">
              Stock Number {!isService && brandCfg.prefix && (
                <span style={{ color: 'var(--accent)', marginLeft: 4 }}>
                  ({brandCfg.prefix} auto-added)
                </span>
              )}
            </label>
            {isService ? (
              <input
                className="afi"
                placeholder="Number plate"
                value={form.stockFree}
                onChange={stockOnChange}
                style={{ textTransform: 'uppercase', letterSpacing: 1 }}
              />
            ) : (
              <div style={{ position: 'relative' }}>
                {brandCfg.prefix && (
                  <span style={{
                    position: 'absolute', left: 13, top: 0, bottom: 0,
                    display: 'flex', alignItems: 'center',
                    fontSize: 13, fontWeight: 700, color: 'var(--muted)',
                    pointerEvents: 'none',
                  }}>{brandCfg.prefix}</span>
                )}
                <input
                  className="afi"
                  placeholder={brandCfg.alphanumeric ? `${brandCfg.digits} chars` : `${brandCfg.digits} digits`}
                  value={form.stockDigits}
                  onChange={stockOnChange}
                  inputMode={brandCfg.alphanumeric ? 'text' : 'numeric'}
                  style={{ paddingLeft: brandCfg.prefix ? 26 : 13, textTransform: brandCfg.alphanumeric ? 'uppercase' : 'none' }}
                />
              </div>
            )}
            <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>
              {brandCfg.stockHint}
            </div>
          </div>
          {dupe && (
            <div className="dupe-warn" style={{ gridColumn: '1 / -1' }}>
              <div className="dupe-head">
                <Warning size={16} weight="fill" />
                Already in the workflow
              </div>
              <div className="dupe-body">
                <strong>{dupe.brand} {dupe.model}</strong>{dupe.colour ? ` · ${titleCase(dupe.colour)}` : ''} — {dupe.stock_number}
                {' '}{stageProgress(dupe) === 100
                  ? 'is finished but not handed over yet'
                  : 'is still being prepared'}
                {dupe.delivery_date ? `, due ${formatDate(dupe.delivery_date)}${dupe.delivery_time ? ' at ' + formatTime(dupe.delivery_time) : ''}` : ''}
                {dupe.salesperson ? ` (${dupe.salesperson})` : ''}.
              </div>
              <div className="dupe-stages">
                {STAGE_COLS.map((c, i) => dupe[c] !== 'na' && (
                  <span key={c} className={`sb ${dupe[c]}`}>{STAGE_LABELS[i]}: {statusLabel(dupe[c])}</span>
                ))}
              </div>
              <div className="dupe-actions">
                <button type="button" className="ab p" onClick={() => onUpdate?.(dupe.id)}>
                  Open that vehicle to edit it
                </button>
                <button type="button" className="ab" onClick={() => setDupeOverride(true)}>
                  It's a different car — add anyway
                </button>
              </div>
            </div>
          )}
          <div>
            <label className="afl">RO Number</label>
            <input
              className="afi"
              placeholder="733779"
              value={form.ro_number}
              onChange={(e) => set('ro_number', e.target.value)}
            />
          </div>
        </div>

        {/* Colour */}
        <div style={{ marginBottom: 14 }}>
          <label className="afl">Colour</label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
            {Object.entries(COLOUR_MAP).map(([name, hex]) => (
              <button
                type="button"
                key={name}
                onClick={() => set('colour', name)}
                title={name}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer',
                  padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                  fontFamily: 'Inter, sans-serif',
                  border: form.colour === name ? '1.5px solid var(--accent)' : '1px solid var(--border)',
                  background: form.colour === name ? 'var(--accent-bg)' : 'var(--bg)',
                  color: 'var(--text)',
                }}
              >
                <span style={{ width: 12, height: 12, borderRadius: '50%', background: hex, border: '1px solid rgba(0,0,0,.15)' }} />
                {name}
              </button>
            ))}
          </div>
          <input
            className="afi"
            placeholder="Or type a custom colour"
            value={form.colour}
            onChange={(e) => set('colour', e.target.value)}
          />
        </div>

        {/* Delivery date + time */}
        <div className="fg2">
          <div>
            <label className="afl">Delivery Date <span className="req">*</span></label>
            <input
              className="afi"
              type="date"
              required
              value={form.delivery_date}
              onChange={(e) => set('delivery_date', e.target.value)}
            />
          </div>
          <div>
            <label className="afl">Delivery Time</label>
            <select
              className="afi"
              style={{ cursor: 'pointer' }}
              value={form.delivery_time}
              onChange={(e) => set('delivery_time', e.target.value)}
            >
              <option value="">— Select time —</option>
              {TIME_SLOTS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>

        {/* Starting location */}
        <div style={{ marginBottom: 14 }}>
          <label className="afl">Starting Location</label>
          <div className="lo3" style={{ flexWrap: 'wrap' }}>
            {LOCATION_IDS.map((loc) => (
              <div
                key={loc}
                className={`lo ${loc} ${form.location === loc ? 'sel' : ''}`}
                style={{ minWidth: 90 }}
                onClick={() => set('location', loc)}
              >
                📍 {LOCATION_LABELS[loc]}
                <br /><small style={{ fontSize: 10, fontWeight: 400 }}>
                  {LOCATION_SUBLABELS[loc] || ''}
                </small>
              </div>
            ))}
          </div>
        </div>

        {/* Services */}
        <div style={{ marginBottom: 14 }}>
          <label className="afl">Services Required</label>
          <div className="fg3">
            {SERVICE_OPTIONS.map((s) => (
              <div
                key={s.id}
                className={`et ${form.services.includes(s.id) ? 'sel' : ''}`}
                onClick={() => toggleService(s.id)}
              >{s.label}</div>
            ))}
          </div>
        </div>

        {/* EV checkbox */}
        <div style={{ marginBottom: 14 }}>
          <label className="afl">
            <input
              type="checkbox"
              checked={form.is_ev}
              onChange={(e) => set('is_ev', e.target.checked)}
              style={{ marginRight: 6 }}
            />
            This is an EV (Electric Vehicle)
          </label>
        </div>

        {/* Notes */}
        <div style={{ marginBottom: 18 }}>
          <label className="afl">Notes for teams</label>
          <textarea
            className="ta"
            placeholder="Special instructions..."
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
          />
        </div>

        <button type="submit" className="btn-in" disabled={saving}>
          {saving ? 'Saving…' : 'Submit Vehicle →'}
        </button>
      </form>
    </div>
  );
}
